import React, { useEffect, useRef, useState } from 'react';
import { Bell, Clock, X, DollarSign } from 'lucide-react';
import { albumAPI } from '../lib/api';
import { useToast } from '../hooks/use-toast';

export default function NotificationBell() {
  const [orders, setOrders] = useState([]);
  const [open, setOpen] = useState(false);
  const lastCountRef = useRef(-1);
  const { toast } = useToast();

  const fetchOrders = async () => {
    try {
      const data = await albumAPI.pendingOrders();
      setOrders(data.orders || []);
      // Notify only after the first successful load, when new orders arrive
      if (lastCountRef.current >= 0 && data.count > lastCountRef.current) {
        const diff = data.count - lastCountRef.current;
        toast({
          title: `Novo pedido de pagamento!`,
          description: `${diff} novo${diff !== 1 ? 's' : ''} pedido${diff !== 1 ? 's' : ''} aguardando liberação.`,
        });
        try {
          // Beep — close the audio context afterwards to avoid leaking one
          // per notification (browsers cap active contexts to ~6).
          const Ctx = window.AudioContext || window.webkitAudioContext;
          const ctx = new Ctx();
          const o = ctx.createOscillator();
          const g = ctx.createGain();
          o.frequency.value = 880;
          o.connect(g); g.connect(ctx.destination);
          g.gain.setValueAtTime(0.05, ctx.currentTime);
          g.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + 0.4);
          o.start(); o.stop(ctx.currentTime + 0.4);
          setTimeout(() => { ctx.close?.().catch(() => {}); }, 600);
        } catch {}
      }
      lastCountRef.current = data.count;
    } catch {}
  };

  useEffect(() => {
    fetchOrders();
    let t;
    const start = () => {
      clearInterval(t);
      // Poll every 30s, but skip while the tab is hidden to save requests
      t = setInterval(() => { if (!document.hidden) fetchOrders(); }, 30000);
    };
    start();
    // Fetch immediately when the tab regains focus (so the photographer sees new
    // orders without waiting for the next tick).
    const onVis = () => { if (!document.hidden) fetchOrders(); };
    document.addEventListener('visibilitychange', onVis);
    return () => {
      clearInterval(t);
      document.removeEventListener('visibilitychange', onVis);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const count = orders.length;

  return (
    <div className="relative">
      <button
        onClick={() => setOpen(v => !v)}
        className="relative btn-outline-dark p-2 rounded-md"
        aria-label={`${count} notificações`}
      >
        <Bell size={18} className={count > 0 ? 'gold-text' : 'text-[#a89e87]'} />
        {count > 0 && (
          <span className="absolute -top-1 -right-1 min-w-[18px] h-[18px] rounded-full bg-red-500 text-white text-[10px] font-bold flex items-center justify-center px-1">
            {count > 9 ? '9+' : count}
          </span>
        )}
      </button>

      {open && (
        <>
          <div className="fixed inset-0 z-40" onClick={() => setOpen(false)} />
          <div className="absolute right-0 top-full mt-2 w-80 max-w-[92vw] card-glow rounded-xl shadow-2xl z-50 overflow-hidden">
            <div className="px-4 py-3 border-b border-[#1c1710] flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Bell size={15} className="gold-text" />
                <span className="font-medium text-[#f2ecdf] text-sm">Pedidos pendentes</span>
              </div>
              <button onClick={() => setOpen(false)} className="text-[#7d735e]"><X size={16} /></button>
            </div>
            {count === 0 ? (
              <div className="p-6 text-center text-sm text-[#a89e87]">
                Nenhum pedido aguardando liberação.
              </div>
            ) : (
              <div className="max-h-96 overflow-y-auto">
                {orders.map(o => (
                  <a
                    key={o.id}
                    href={`/galeria/${o.albumCode}`}
                    onClick={() => setOpen(false)}
                    className="block px-4 py-3 border-b border-[#1c1710] last:border-b-0 hover:bg-[#1a140c] transition-colors"
                  >
                    <div className="flex items-start justify-between gap-2">
                      <div className="flex-1 min-w-0">
                        <div className="text-sm text-[#f2ecdf] truncate">{o.albumTitle}</div>
                        <div className="text-xs text-[#a89e87] mt-0.5 truncate">
                          {o.mode === 'full' ? 'Álbum completo' : `${o.count} foto${o.count !== 1 ? 's' : ''}`}
                          {o.clientName && ` • ${o.clientName}`}
                        </div>
                        <div className="text-[10px] text-[#7d735e] mt-0.5 flex items-center gap-1">
                          <Clock size={10} /> {new Date(o.createdAt).toLocaleString('pt-BR')}
                        </div>
                      </div>
                      <div className="text-right flex-shrink-0">
                        <div className="gold-text font-semibold text-sm flex items-center gap-1">
                          <DollarSign size={12} />
                          {Number(o.amount).toFixed(2)}
                        </div>
                      </div>
                    </div>
                  </a>
                ))}
              </div>
            )}
            <div className="px-4 py-2 text-[11px] text-[#7d735e] text-center border-t border-[#1c1710]">
              Atualiza automaticamente a cada 30 segundos
            </div>
          </div>
        </>
      )}
    </div>
  );
}
